import { Add, Delete } from "@mui/icons-material";
import { Box, Button, Grid, IconButton, TextField } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DepartureTimeExp, MinDate, TicketType, TransportationMethod, TransportationType, ValidTextRegExp, ValidTicketNumberRegExp } from "../../../Constant";
import {
  getBusTransportationOption,
  getSingleTicketTypeOption,
  getTicketTypeOptions,
  getTrainTransportationOption
} from "../../../Utils";
import LineApiService from "../../../adapters/xhr/LineApiService";
import { MyDatePicker } from "../../../components/my-date-picker/MyDatePicker";
import { MyDropdown } from "../../../components/my-dropdown/MyDropdown";
import { MyTextField } from "../../../components/my-text-field/MyTextField";
import { MyTimePicker } from "../../../components/my-time-picker/MyTimePicker";
import MyContext from "../../../contexts/MyContext";
import { FormWrapper } from "../FormWrapper";
import { AddTransportReceiptDialog } from "../dialogs/AddTransportReceiptDialog";

export const TicketForm1 = (props) => {
  const { t, i18n } = useTranslation();
  const myContext = useContext(MyContext);
  const [biljettnummerValueErr, setBiljettnummerValueErr] = useState("");

  const [addTransportReceipt, setAddTransportReceipt] = useState(false);
  const [fromValueErr, setFromValueErr] = useState("");
  const [toValueErr, setToValueErr] = useState("");
  const [departureDateValueErr, setDepartureDateValueErr] = useState("");
  const [departureTimeValueErr, setDepartureTimeValueErr] = useState(null);
  const [lineValueErr, setLineValueErr] = useState("");

  const singleTicketTypeOption = getSingleTicketTypeOption();
  const commuterTicketTypeOption = getTicketTypeOptions().find((option) => option.ticketType === TicketType.COMMUTER);
  const busTransportationTypeOption = getBusTransportationOption();
  const trainTransportationTypeOption = getTrainTransportationOption();

  const [line, setLine] = useState(null);
  const [stopOptionsOnLine, setstopOptionsOnLine] = useState([]);
  const [ticketType, setTicketType] = React.useState(singleTicketTypeOption);
  const [transportationMethod, setTransportationMethod] = useState(busTransportationTypeOption);

  const today = new Date();
  const minDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - MinDate, 0, 0, 0);

  const lineOptions = useMemo(() => {
    let options = [];
    switch (myContext.transportationMethod) {
      case TransportationMethod.BUS:
        options = myContext.lines.filter(line => line.defaultTransportModeCode === "BUS");
        break;
      case TransportationMethod.TRAIN:
        options = myContext.lines.filter(line => line.defaultTransportModeCode === "TRAIN");
        break;
      default:
        options = [];
        break;
    }
    options = options.map((option) => {
      let rObj = {};
      rObj["key"] = `${option.number}-${option.name}`;
      rObj["value"] = option.number == option.name ? option.number : `${option.number} (${option.name})`;
      rObj["number"] = `${option.number}`;
      rObj["name"] = `${option.name}`;
      return rObj;
    });
    return options;
  }, [myContext.transportationMethod, myContext.lines]);

  useEffect(() => {
    if (myContext.ticketType === TicketType.SINGLE) setTicketType(singleTicketTypeOption);
    if (myContext.ticketType === TicketType.COMMUTER) setTicketType(commuterTicketTypeOption); 
  }, [myContext.ticketType]);

  useEffect(() => {
    if (myContext.transportationMethod === TransportationMethod.BUS) setTransportationMethod(busTransportationTypeOption);
    if (myContext.transportationMethod === TransportationMethod.TRAIN) setTransportationMethod(trainTransportationTypeOption);
  }, [myContext.transportationMethod]);

  const fetchStopsOnLine = async () => {
      let response = await LineApiService.getStopsOnLine(myContext.line.number);
      if (response && response.length > 0) {
          let data = response.map((option, key) => {
              let rObj = {};
              rObj["key"] = `${option.stopAreaName}-${option.zoneName}`;
              rObj["value"] = `${option.stopAreaName}`;
              rObj["zone"] = `${option.zoneName}`;
              rObj["stopArea"] = `${option.stopAreaName}`;
              rObj["stopNumber"] = `${option.stopNumber}`;
              return rObj;
          });
          setstopOptionsOnLine(data);
          myContext.changeFrom(null);
          myContext.changeTo(null);
      }
  };

  useEffect(() => {
      if (myContext.line) fetchStopsOnLine();
  }, [myContext.line]);

  const handleChangeDepartureTime = (value) => {
    let departureTimeReg = new RegExp(DepartureTimeExp);
    if (departureTimeReg.test(value)) {
      myContext.changeDepartureTime(value);
    } else myContext.changeDepartureTime(null);
  };

  const validate = async () => {
    let isValid = true;
    if (!myContext.biljettnummerValue?.trim()) {
      isValid = false;
      setBiljettnummerValueErr("ticketnumber_must_be_entered");
    } else {
      isValid = true;
      setBiljettnummerValueErr("");
    }

    if (!myContext.line) {
      setLineValueErr("line_must_be_specified");
      isValid = false;
    } else {
      setLineValueErr("");
    }

    if (!myContext.from) {
      setFromValueErr("from_must_be_specified");
      isValid = false;
    } else {
      setFromValueErr("");
    }
    if (!myContext.to) {
      setToValueErr("to_must_be_specified");
      isValid = false;
    } else {
      setToValueErr("");
    }
    if (!myContext.departureDate || myContext.departureDate.trim() === "") {
      setDepartureDateValueErr("departure_date_must_be_specified");
      isValid = false;
    } else if (!validateDepartureDate()) {
      isValid = false;
    } else {
      setDepartureDateValueErr("");
    }

    if (!myContext.departureTime) {
      setDepartureTimeValueErr("departure_time_must_be_specified");
      isValid = false;
    } else {
      setDepartureTimeValueErr("");
    }
    if (isValid) myContext.changeActiveSlide((myContext.activeSlide += 1));
  };

  const removeTransportReceipt = () => {
    myContext.changeTransportReceipt([]);
    myContext.changeTransportTotalCost("");
    myContext.changeFromAddress("");
    myContext.changeToAddress("");
    myContext.changeNumberOfKmDriven("");
  };

  const validateDepartureDate = () => {
    let date = myContext.departureDate;
    if (date != null) {
      let time = new Date(date).getTime();
      let minTime = new Date(minDate).getTime();
      let maxTime = new Date(today).getTime();
      myContext.changeDepartureDate(date);
      if (time >= minTime && time <= maxTime) {
        setDepartureDateValueErr("");
        return true;
      } else {
        setDepartureDateValueErr("register_dates_up_to_min_days_older_than_today_date");
        return false;
      }
    }
  };

  useEffect(() => {
    validateDepartureDate();
  }, [myContext.departureDate]);

  return (
    <MyContext.Consumer>
      {(context) => (
        <React.Fragment>
          <FormWrapper onContinue={validate} hasBackButton={false} number={`1/2`} heading={`${t("ticket_info")}`}> 
            <Autocomplete
              style={{ width: "100%", margin: "1rem 0", ...props.style }}
              options={[busTransportationTypeOption, trainTransportationTypeOption]}
              getOptionLabel={(option) => t(option.title)}
              value={transportationMethod}
              onChange={(e, value) => {
                  if (value.transportationMethod !== transportationMethod.transportationMethod) {
                    context.changeTransportationMethod(value.transportationMethod);
                    setTransportationMethod(value);
                    context.changeFrom("");
                    context.changeTo("");
                    context.changeLine("");
                    setLine(null);
                    context.changeBiljettnummerValue("");
                    context.changeDepartureDate(null);
                    context.changeDepartureTime(null);
                    if (value.transportationMethod == TransportationMethod.TRAIN && context.ticketType == TicketType.SINGLE) {
                      context.changeTicketType(commuterTicketTypeOption.ticketType);
                      setBiljettnummerValueErr("");
                      setTicketType(commuterTicketTypeOption);
                      context.changeTicketId(null);
                    }
                    else if (value.transportationMethod == TransportationMethod.BUS && context.ticketType == TicketType.COMMUTER) {
                        context.changeTicketType(singleTicketTypeOption.ticketType);
                        setBiljettnummerValueErr("");
                        setTicketType(singleTicketTypeOption);
                        context.changeTicketId(null);
                    }
                }
              }}
              disableClearable={true}
              renderInput={(params) => <TextField {...params} label={t("transportation_fardmedel")} variant="outlined" />}
            />
            <Autocomplete
              style={{ width: "100%", margin: "1rem 0", ...props.style }}
              options={
                context.transportationMethod == TransportationMethod.BUS
                  ? [singleTicketTypeOption, commuterTicketTypeOption]
                  : [commuterTicketTypeOption]
              }
              getOptionLabel={(option) => t(option.title)}
              value={ticketType}
              onChange={(e, value) => {
                context.changeTicketType(value.ticketType);
                setBiljettnummerValueErr("");
                setTicketType(value);
                context.changeTicketId(null);
              }}
              disableClearable={true}
              renderInput={(params) => <TextField {...params} label={t("ticket_type")} variant="outlined" />}
            />
            <MyTextField
              helperText={t(biljettnummerValueErr)}
              autoComplete="on"
              regExp={ValidTicketNumberRegExp}
              maxLength={14}
              value={context.biljettnummerValue}
              label={t("ticket_number")}
              required={true}
              onChange={context.changeBiljettnummerValue}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  validate();
                }
              }}
            />
            <MyDropdown
              required={true}
              helperText={t(lineValueErr)}
              onChange={(e, value) => {
                context.changeLine(value);
                setLine(value);
              }}
              label={t("line")}
              value={line}
              options={lineOptions}
            />            
            <MyDropdown
              required={true}
              helperText={t(fromValueErr)}
              onChange={(e, value) => context.changeFrom(value)}
              label={t("from")}
              value={context.from}
              options={stopOptionsOnLine.filter((option) => option.key !== context.to?.key)}
            />
            <MyDropdown
              required={true}
              helperText={t(toValueErr)}
              onChange={(e, value) => context.changeTo(value)}
              label={t("to")}
              value={context.to}
              options={stopOptionsOnLine.filter((option) => option.key !== context.from?.key)}
            />
            <MyDatePicker
              required={true}
              error={departureDateValueErr !== ""}
              label={t("departure_date")}
              value={context.departureDate}
              onAccept={(date) => context.changeDepartureDate(date?.toString())}
              helperText={t(departureDateValueErr)}
              maxDate={today}
              minDate={minDate}
              placeholder={t("date_format")}
              cancelLabel={t("cancel")}
              okLabel={t("ok")}
              locale={i18n.language}
              views={["day"]}
            />
            {context.departureTimeOptions == null || context.departureTimeOptions.length == 0 ? (
              <MyTimePicker
                required={true}
                onAccept={(date) => context.changeDepartureTime(date?.toString())}
                value={context.departureTime}
                label={t("departure_time")}
                helperText={t(departureTimeValueErr)}
              />
            ) : (
              <MyDropdown
                required={true}
                helperText={t(departureTimeValueErr)}
                onChange={(e, time) => handleChangeDepartureTime(time !== null ? time.value : null)}
                label={t("departure_time")}
                freeSolo={true}
                placeholder={`HH:mm`}
                onFreeSolo={(e) => handleChangeDepartureTime(e.target.value)}
                value={context.departureTime}
                options={context.departureTimeOptions}
              />
            )}
            <h2 style={{ fontSize: "1.25rem", width: "100%", marginBottom: "0.5rem" }}>{t("do_you_have_additional_costs")}</h2>
            <hr className="margin top-0 bottom-0" aria-hidden={true} style={{ width: "100%" }} />
            <Grid container>
              <Grid item xs={context.transportTotalCost || context.numberOfKmDriven ? 11 : 12} style={{ display: "flex" }}>
                <Button
                  aria-expanded={addTransportReceipt}
                  aria-haspopup="dialog"
                  variant="text"
                  className="button secondary w-100"
                  style={{
                    textTransform: "unset",
                  }}
                  sx={{
                    "& span": {
                      color: "primary.main",
                    },
                    "& svg": {
                      color: "primary.main",
                    },
                  }}
                  onClick={(e) => setAddTransportReceipt(true)}
                  startIcon={<Add />}
                >
                  <span>{t("add_transport_expenses")}</span>
                  <div style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
                    <Box component="span" color="primary.main">
                      {context.transportationType == TransportationType.OTHERS && context.transportTotalCost && `${context.transportTotalCost} ${t("kr")}`}
                      {context.transportationType == TransportationType.USEOWNCAR && context.numberOfKmDriven && `${context.numberOfKmDriven} ${t("km")}`}
                    </Box>
                  </div>
                </Button>
              </Grid>
              {(context.transportTotalCost || context.numberOfKmDriven) && (
                <Grid item xs={1}>
                  <IconButton aria-label={t("delete")} onClick={(e) => removeTransportReceipt()}>
                    <Delete />
                  </IconButton>
                </Grid>
              )}
            </Grid>
            <hr className="margin top-0 bottom-0" aria-hidden={true} style={{ width: "100%" }} />
            {((context.transportationType === TransportationType.OTHERS && context.transportReceipt.length > 0) ||
              (context.transportationType === TransportationType.USEOWNCAR && context.numberOfKmDriven)) && (
              <MyTextField
                maxLength={500}
                regExp={ValidTextRegExp}
                value={context.otherComments}
                onChange={context.changeOtherComments}
                label={t("other_comments")}
                multiline={true}
                rows={4}
                style={{ marginBottom: "0" }}
              />
            )}
          </FormWrapper>
          {addTransportReceipt && <AddTransportReceiptDialog open={addTransportReceipt} onClose={(e) => setAddTransportReceipt(false)} />}
        </React.Fragment>
      )}
    </MyContext.Consumer>
  );
};
